import {map} from 'rxjs/operators';
import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Parameter} from '../models/parameter';
import {Configuration} from '../models/configuration';
import {ParameterService} from '../services/parameter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Alert, AlertType} from '../../shared/alert.model';
import {MessageService} from '../../shared/message.service';
import {getAlertMessage} from '../../shared/api-loaders';
import {Observable} from 'rxjs';
import {MessageType} from '../../shared/messageType';
import {Select2Option} from '../../shared/select2-option';
import {ClaimsService} from '../../shared/claims.service';
import {CustomValidators} from '../validators/blank-validator';
import {ParameterValueValidator} from '../validators/parameter-value-validator';
import {SecurityService} from '../../shared/security.service';
import {ConfigurationService} from '../services/configuration.service';
import {CustomerService} from '../services/customer.service';

@Component({
    selector: 're-parameter-detail',
    templateUrl: './parameter-detail.component.html',
    styleUrls: ['./parameter-detail.component.scss'],
    standalone: false
})
export class ParameterDetailComponent implements OnInit {

  id: number;
  parameter= new Parameter();
  configuration: Configuration;
  public parameterId: number;
  public corpOid: any=null;
  public snfAppUrl: any=null;

  alert: Alert;
  successAlert: Alert;

  showMessage = false;
  hasGlobalError = false;
  editProfile: boolean;
  modalReference: any;
  username: string;
  enabledDisableData: Select2Option[];
  gsEnabledData: Select2Option[];
  gsEnabled: boolean;
  isReadOnlyUser: boolean;

  parameterDetailForm: UntypedFormGroup = this.fb.group({
    id: [''],
    configurationId: [''],
    key: ['', CustomValidators.blankValidator],
    value: ['',null, _ => ParameterValueValidator.validate2(this.parameterDetailForm?.controls['value'],this.parameterDetailForm?.controls['key'],this.corpOid,this.snfAppUrl,this.configurationService, this.customerService, this.parameterDetailForm?.controls['configurationId'].value,null,false)],
    environmentKey: ['']
  });

  parameterKeysForm: UntypedFormGroup = this.fb.group({
    corpOid: [this.corpOid],
    snfAppUrl: [this.snfAppUrl]
  });

  public configurations = [];

  constructor(
    private route: ActivatedRoute,
    private parameterService: ParameterService,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private modalService: NgbModal,
    private messageService: MessageService,
    private claimsService: ClaimsService,
    private router: Router,
    private securityService: SecurityService,
    private configurationService: ConfigurationService,
    private customerService: CustomerService
  ) {
    this.parameterId = +this.route.snapshot.paramMap.get('id');
    if (!this.parameterId) {
      this.editProfileClicked();
    }
  }

  async ngOnInit() {
    this.isReadOnlyUser = await this.securityService.isThisReadOnlyUser();
    this.username = this.claimsService.getPreferredName();
    await this.loadOptions();
    this.getParameter();
  }

  private async  loadOptions() {
    await this.parameterService.getEnableDisableOptions().pipe(map(data => {
      this.enabledDisableData = data
    })).toPromise();
    await this.parameterService.getGSDisplayOptions().pipe(map(data => {
      this.gsEnabledData = data
    })).toPromise();
  }

  async refreshForm(parameter) {
    let data = await this.parameterService.getParametersByStack().toPromise();
      this.configurations = data.filter(param => param.id === this.parameterId);
      this.parameter = parameter;

      if(this.parameter.key.toLowerCase().includes('bp.globalsearch.enabled')){
        this.checkGSDependencies();
      }

    this.parameterDetailForm.patchValue({
      id: parameter.id,
      key: parameter.key,
      value: parameter.value,
      configurationId: parameter.configurationId,
      environmentKey: parameter.environmentKey,
      corpOid: parameter.corpOid
    });
  }

  goBack(): void {
    this.location.back();
  }

  clearAlerts() {
    this.alert = null;
    this.hasGlobalError = false;
    setTimeout(() => this.showMessage = false, 5000);
  }

  editProfileClicked() {
    this.editProfile = true;
  }

  resetFlags() {
    if (!this.parameterId) {
      this.goBack();
      return;
    }
    this.editProfile = false;
    this.getParameter();
  }

  async getParameter() {
    if (!this.parameterId){
      this.parameter.configurationId = +this.route.snapshot.params.configurationId;
      this.checkGSDependencies();
      return;
    }
    this.parameterService.getParameter(this.parameterId).subscribe(parameter => this.refreshForm(parameter));
  }

  // TODO: Make use of the functions available in user-list component with component to component interaction to avoid code duplication
  updateParameter(): void {
    this.parameterDetailForm.value.key = this.parameterDetailForm.value.key.trim();
    this.parameterDetailForm.value.value = this.parameterDetailForm.value.value.trim();
    const parameter = new Parameter(this.parameterDetailForm.value);

    let obs: Observable<Parameter>;
    if (this.parameterId) {
      parameter.modifiedBy = this.username;
      obs = this.parameterService.updateParameter(this.parameterId, parameter);
    } else {
      parameter.configurationId = +this.route.snapshot.params.configurationId;
      parameter.createdBy = this.username;
      delete parameter.environmentKey;
      delete parameter.corpOid;
      delete parameter.id;
      obs = this.parameterService.addParameter(parameter);
    }

    obs.subscribe(
      configuration => {
        // If customer was updated successfully, id must have some value.
        if (this.parameterId) {
          this.resetFlags();
          this.successAlert = { message: `Parameter ${parameter.key} has been updated successfully`, type: AlertType.SUCCESS };

          this.clearAlerts();
        } else {

          this.router.navigate(['..'], { relativeTo: this.route }).then(status => {
            this.messageService.send({ type: MessageType.ParameterCreated, data: parameter.key });
          });
        }
      },
      err => {
        this.alert = getAlertMessage(err);
      }
    );
  }

  deleteParameter(): void {
    const id = +this.parameterDetailForm.get('id').value;
    this.parameterService
      .deleteParameter(id)
      .subscribe(
        res => {
          this.goBack();
        },
        error => {
          this.alert = getAlertMessage(error);
        }
      );
  }

  public checkSelect() {

    if (this.parameterDetailForm.get('key').value.includes('bp.globalsearch.displayformat')){
      this.gsEnabled = true;
      return true;
    } else if (this.parameterDetailForm.get('key').value.includes('enabled') || this.parameterDetailForm.get('key').value.includes('isdefault')) {
      return true;
    } else{
      this.gsEnabled = false;
      return false;
    }
  }
  public checkDefaultText() {
    if (this.parameterDetailForm.get('key').value.includes('bp.globalsearch.displayformat')){
      this.gsEnabled = true;
      return 'First Name \"Preferred Name\" Last Name';
    } else{
      this.gsEnabled = false;
      return 'False';
    }
  }

  public async checkGSDependencies() {
    let parameter2 = await this.parameterService.getParameterByKey(String(this.parameter.configurationId),"snf.corpOid,snf.app.url.root").toPromise();
    for (var i = 0; i < parameter2.length; i++) {
      let option = parameter2[i];
      if (option.key.toLowerCase().includes("snf.corpoid")) {
        this.corpOid= option.value;
      }
      if (option.key.toLowerCase().includes("snf.app.url.root")) {
        this.snfAppUrl = option.value;
      }
    }

    this.parameterKeysForm.patchValue({
      corpOid: this.corpOid,
      snfAppUrl: this.snfAppUrl
    });
  }
}
